import { CommonModule } from '@angular/common'
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, computed, OnDestroy, OnInit, signal, ViewChild } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { EventWizardComponent } from '@app/v2/event/event-wizard/event-wizard.component'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { AuthService } from '../../../auth/services/auth.service'
import { ServerRepresentedEvent } from '../../../events/models/event.model'
import { EventService } from '../../../events/services/event.service'
import { Workspace } from '../../../workspaces/interfaces/workspace.interface'
import { WorkspaceService } from '../../../workspaces/services/workspace.service'
import { EventMigrationService } from '../../services/event-migration.service'

@Component({
  selector: 'app-migrate-events',
  standalone: true,
  imports: [FormsModule, DropdownComponent, ReactiveFormsModule, SelectInputComponent],
  templateUrl: './migrate-events.component.html',
  styleUrl: './migrate-events.component.scss',
  providers: [EventService]
})
export class MigrateEventsComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('sourceOrgDropdown') sourceOrgDropdown?: DropdownComponent
  @ViewChild('sourceEventDropdown') sourceEventDropdown?: DropdownComponent
  @ViewChild('targetOrgDropdown') targetOrgDropdown?: DropdownComponent
  @ViewChild('sourceOrgSelectInput') sourceOrgSelectInput?: SelectInputComponent
  @ViewChild('sourceEventSelectInput') sourceEventSelectInput?: SelectInputComponent
  @ViewChild('targetOrgSelectInput') targetOrgSelectInput?: SelectInputComponent

  // Source - FormControls for select-input
  public sourceOrgControl = new FormControl('')
  public sourceOrgValues = signal<Workspace[]>([])
  public sourceEventControl = new FormControl('')
  public sourceEventValues = signal<ServerRepresentedEvent[]>([])

  // Legacy signals for compatibility
  public sourceOrgSearch = signal<string>('')
  public sourceOrgOptions = signal<DropdownOption[]>([])
  public selectedSourceOrg = signal<Workspace | null>(null)
  public sourceEventSearch = signal<string>('')
  public sourceEventOptions = signal<DropdownOption[]>([])
  public selectedSourceEvent = signal<ServerRepresentedEvent | null>(null)
  public isLoadingSourceOrgs = signal<boolean>(false)
  public isLoadingSourceEvents = signal<boolean>(false)
  // Track if we've attempted to load to show appropriate messages
  public hasAttemptedLoadSourceOrgs = false
  public hasAttemptedLoadSourceEvents = false

  // Target - FormControls for select-input
  public targetOrgControl = new FormControl('')
  public targetOrgValues = signal<Workspace[]>([])

  // Legacy signals for compatibility
  public targetOrgSearch = signal<string>('')
  public targetOrgOptions = signal<DropdownOption[]>([])
  public selectedTargetOrg = signal<Workspace | null>(null)
  public isLoadingTargetOrgs = signal<boolean>(false)

  public canSelectSourceEvent = computed(() => this.selectedSourceOrg() !== null)
  public canSelectTarget = computed(() => this.selectedSourceOrg() !== null && this.selectedSourceEvent() !== null)
  public canMigrate = computed(() => this.selectedSourceOrg() !== null && this.selectedSourceEvent() !== null && this.selectedTargetOrg() !== null)

  private _destroy$ = new Subject<void>()
  private _sourceOrgSearch$ = new Subject<string>()
  private _sourceEventSearch$ = new Subject<string>()
  private _targetOrgSearch$ = new Subject<string>()

  // Pagination state
  private _sourceOrgs: Workspace[] = []
  private _sourceOrgPage = 0
  private _hasMoreSourceOrgs = true
  private _sourceEvents: ServerRepresentedEvent[] = []
  private _sourceEventPage = 0
  private _hasMoreSourceEvents = true
  private _targetOrgs: Workspace[] = []
  private _targetOrgPage = 0
  private _hasMoreTargetOrgs = true

  // Scroll handlers
  private _sourceOrgScrollHandler?: (e: Event) => void
  private _sourceEventScrollHandler?: (e: Event) => void
  private _targetOrgScrollHandler?: (e: Event) => void
  private _targetOrgFocusListenerSetup = false

  private _isMigrating = false

  constructor(
    private _workspaceService: WorkspaceService,
    private _eventService: EventService,
    private _eventMigrationService: EventMigrationService,
    private _alertService: AlertService,
    private _router: Router,
    private _authService: AuthService,
    private _wizardService: WizardService,
    private _appService: AppService,
    private _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initially disable source event control
    this.sourceEventControl.disable()

    this._setupSearchSubscription(this._sourceOrgSearch$, (term) => {
      this.sourceOrgSearch.set(term)
      // Reset hasAttemptedLoad FIRST, then set loading state to ensure "Loading..." shows immediately
      this.hasAttemptedLoadSourceOrgs = false
      this.isLoadingSourceOrgs.set(true)
      this._cdr.detectChanges()
      this._resetSourceOrgs()
      // Always load when search changes (including when cleared to empty string)
      this._loadSourceOrgs()
    })

    this._setupSearchSubscription(this._sourceEventSearch$, (term) => {
      this.sourceEventSearch.set(term)
      // Reset hasAttemptedLoad FIRST, then set loading state to ensure "Loading..." shows immediately
      if (this.canSelectSourceEvent()) {
        this.hasAttemptedLoadSourceEvents = false
        this.isLoadingSourceEvents.set(true)
        this._cdr.detectChanges()
        this._resetSourceEvents()
        // Always load when search changes (including when cleared to empty string)
        this._loadSourceEvents()
      } else {
        this._resetSourceEvents()
      }
    })

    this._setupSearchSubscription(this._targetOrgSearch$, (term) => {
      this.targetOrgSearch.set(term)
      // Set loading state FIRST before resetting to ensure "Loading..." shows immediately
      if (this.canSelectTarget()) {
        if (!this.isLoadingTargetOrgs()) {
          this.isLoadingTargetOrgs.set(true)
          this._cdr.detectChanges()
        }
        this._resetTargetOrgs()
        // Always load when search changes (including when cleared to empty string)
        this._loadTargetOrgs()
      } else {
        this._resetTargetOrgs()
      }
    })
  }

  ngAfterViewInit(): void {
    // Set up focus listeners for select-input components
    setTimeout(() => {
      this._setupSelectInputFocusListeners()
    }, 100)
  }

  ngAfterViewChecked(): void {
    // Set up target org focus listener when field becomes available
    if (this.canSelectTarget() && !this._targetOrgFocusListenerSetup) {
      const targetOrgInput = this.targetOrgSelectInput?.selectInput?.nativeElement
      if (targetOrgInput) {
        this._targetOrgFocusListenerSetup = true
        targetOrgInput.addEventListener('focus', () => {
          if (this.targetOrgValues().length === 0 && !this.isLoadingTargetOrgs()) {
            this._loadTargetOrgs()
          }
          // Set up scroll listener when dropdown opens
          setTimeout(() => this._setupScrollListener('targetOrg'), 300)
        })
      }
    }
  }

  /**
   * Sets up focus listeners for select-input components to load data on focus.
   */
  private _setupSelectInputFocusListeners(): void {
    // Source Organization
    const sourceOrgInput = this.sourceOrgSelectInput?.selectInput?.nativeElement
    if (sourceOrgInput) {
      sourceOrgInput.addEventListener('focus', () => {
        if (this.sourceOrgValues().length === 0 && !this.isLoadingSourceOrgs()) {
          // Set loading state immediately and trigger change detection
          this.isLoadingSourceOrgs.set(true)
          this._cdr.detectChanges()
          // Small delay to ensure dropdown reads updated optionNoData, then load
          setTimeout(() => {
            this._loadSourceOrgs()
          }, 0)
        }
        // Set up scroll listener when dropdown opens
        setTimeout(() => this._setupScrollListener('sourceOrg'), 200)
      })
    }

    // Source Event
    const sourceEventInput = this.sourceEventSelectInput?.selectInput?.nativeElement
    if (sourceEventInput) {
      sourceEventInput.addEventListener('focus', () => {
        if (this.sourceEventValues().length === 0 && !this.isLoadingSourceEvents() && this.canSelectSourceEvent()) {
          // Set loading state immediately and trigger change detection
          this.isLoadingSourceEvents.set(true)
          this._cdr.detectChanges()
          // Small delay to ensure dropdown reads updated optionNoData, then load
          setTimeout(() => {
            this._loadSourceEvents()
          }, 0)
        }
        // Set up scroll listener when dropdown opens
        setTimeout(() => this._setupScrollListener('sourceEvent'), 200)
      })
    }

    // Target Organization
    const targetOrgInput = this.targetOrgSelectInput?.selectInput?.nativeElement
    if (targetOrgInput) {
      targetOrgInput.addEventListener('focus', () => {
        if (this.targetOrgValues().length === 0 && !this.isLoadingTargetOrgs()) {
          // Set loading state immediately and trigger change detection
          this.isLoadingTargetOrgs.set(true)
          this._cdr.detectChanges()
          // Small delay to ensure dropdown reads updated optionNoData, then load
          setTimeout(() => {
            this._loadTargetOrgs()
          }, 0)
        }
        // Set up scroll listener when dropdown opens
        setTimeout(() => this._setupScrollListener('targetOrg'), 300)
      })
    }
  }

  ngOnDestroy(): void {
    this._cleanupScrollListeners()
    this._destroy$.next()
    this._destroy$.complete()
  }

  /**
   * Handles the input event for the source organization dropdown.
   * @param {Event} event - The event that triggered the input.
   */
  public onSourceOrgInput(event: Event): void {
    this._sourceOrgSearch$.next((event.target as HTMLInputElement).value)
  }

  /**
   * Clears the source organization.
   */
  public clearSourceOrg(): void {
    this.sourceOrgSearch.set('')
    this._sourceOrgSearch$.next('')
    this.selectedSourceOrg.set(null)
    this._resetSourceOrgs()
    this._resetSourceEvents()
    this.selectedSourceEvent.set(null)
    this.sourceEventSearch.set('')
  }

  /**
   * Handles the dropdown opened event for the source organization dropdown.
   */
  public onSourceOrgDropdownOpened(): void {
    if (this.sourceOrgValues().length === 0 && !this.isLoadingSourceOrgs()) {
      this.isLoadingSourceOrgs.set(true)
      this._loadSourceOrgs()
    }
    setTimeout(() => this._setupScrollListener('sourceOrg'), 100)
  }

  /**
   * Handles the dropdown closed event for the source organization dropdown.
   */
  public onSourceOrgDropdownClosed(): void {
    this._cleanupScrollListener('sourceOrg')
  }

  /**
   * Handles the option selected event for the source organization dropdown.
   * @param {DropdownOption} option - The option that was selected.
   */
  public onSourceOrgSelected(option: DropdownOption): void {
    const org = option.data as Workspace
    this.selectedSourceOrg.set(org)
    this.sourceOrgSearch.set(this._getOrgName(org))
    this._resetSourceEvents()
    this.selectedSourceEvent.set(null)
    this.sourceEventSearch.set('')

    // Reset target if it matches source
    if (this.selectedTargetOrg()?.id === org.id) this.clearTargetOrg()
    else this._resetTargetOrgs()
  }

  /**
   * Handles the focus event for the source event dropdown.
   * @param {Event} [event] - The event that triggered the focus.
   */
  public onSourceEventFocus(event?: Event): void {
    if (!this.canSelectSourceEvent()) return
    if (this._sourceEvents.length === 0) this._loadSourceEvents()
    this.sourceEventDropdown?.toggleDropdown(event || new Event('click'))
  }

  /**
   * Handles the input event for the source event dropdown.
   * @param {Event} event - The event that triggered the input.
   */
  public onSourceEventInput(event: Event): void {
    this._sourceEventSearch$.next((event.target as HTMLInputElement).value)
  }

  /**
   * Clears the source event.
   */
  public clearSourceEvent(): void {
    this.sourceEventSearch.set('')
    this._sourceEventSearch$.next('')
    this.selectedSourceEvent.set(null)
    this._resetSourceEvents()
    this.clearTargetOrg()
  }

  /**
   * Handles the dropdown opened event for the source event dropdown.
   */
  public onSourceEventDropdownOpened(): void {
    if (this.canSelectSourceEvent() && this.sourceEventValues().length === 0 && !this.isLoadingSourceEvents()) {
      this.isLoadingSourceEvents.set(true)
      this._loadSourceEvents()
    }
    setTimeout(() => this._setupScrollListener('sourceEvent'), 100)
  }

  /**
   * Handles the dropdown closed event for the source event dropdown.
   */
  public onSourceEventDropdownClosed(): void {
    this._cleanupScrollListener('sourceEvent')
  }

  /**
   * Handles the option selected event for the source event dropdown.
   * @param {DropdownOption} option - The option that was selected.
   */
  public onSourceEventSelected(option: DropdownOption): void {
    const event = option.data as ServerRepresentedEvent
    this.selectedSourceEvent.set(event)
    this.sourceEventSearch.set(event.name || '')
  }

  /**
   * Handles the focus event for the target organization dropdown.
   * @param {Event} [event] - The event that triggered the focus.
   */
  public onTargetOrgFocus(event?: Event): void {
    if (this._targetOrgs.length === 0) this._loadTargetOrgs()
    this.targetOrgDropdown?.toggleDropdown(event || new Event('click'))
  }

  /**
   * Handles the input event for the target organization dropdown.
   * @param {Event} event - The event that triggered the input.
   */
  public onTargetOrgInput(event: Event): void {
    this._targetOrgSearch$.next((event.target as HTMLInputElement).value)
  }

  /**
   * Clears the target organization.
   */
  public clearTargetOrg(): void {
    this.targetOrgSearch.set('')
    this._targetOrgSearch$.next('')
    this.selectedTargetOrg.set(null)
    this.targetOrgControl.setValue('', { emitEvent: true })
    this._resetTargetOrgs()
  }

  /**
   * Handles the dropdown opened event for the target organization dropdown.
   */
  public onTargetOrgDropdownOpened(): void {
    this._loadTargetOrgs()
    setTimeout(() => this._setupScrollListener('targetOrg'), 100)
  }

  /**
   * Handles the dropdown closed event for the target organization dropdown.
   */
  public onTargetOrgDropdownClosed(): void {
    this._cleanupScrollListener('targetOrg')
  }

  /**
   * Handles the option selected event for the target organization dropdown.
   * @param {DropdownOption} option - The option that was selected.
   */
  public onTargetOrgSelected(option: DropdownOption): void {
    const org = option.data as Workspace
    const sourceOrg = this.selectedSourceOrg()

    // Prevent selecting same as source
    if (sourceOrg && sourceOrg.id === org.id) return

    this.selectedTargetOrg.set(org)
    this.targetOrgSearch.set(this._getOrgName(org))
  }

  /**
   * Handles value changed event from select-input for source organization.
   * @param {number | string | null} value - The selected organization ID.
   */
  public onSourceOrgValueChanged(value: number | string | null): void {
    if (!value) {
      this.selectedSourceOrg.set(null)
      this.sourceOrgControl.setValue('')
      // Reset source event when source org is cleared
      this.selectedSourceEvent.set(null)
      this.sourceEventControl.setValue('', { emitEvent: true })
      this.sourceEventSearch.set('')
      this._resetSourceEvents()
      this._clearSourceEventDisplay()
      return
    }
    const org = this.sourceOrgValues().find((o) => o.id === value)
    if (org) {
      // Check if organization is actually changing (not just re-selecting the same one)
      const previousOrg = this.selectedSourceOrg()
      const previousOrgId = previousOrg?.id
      const isOrgChanging = !previousOrg || previousOrgId !== org.id

      // Reset source event when source org changes
      if (isOrgChanging) {
        if (this.selectedSourceEvent() !== null) {
          this.selectedSourceEvent.set(null)
          this.sourceEventControl.setValue('', { emitEvent: true })
          this.sourceEventSearch.set('')
        }
        this._resetSourceEvents()
        this._clearSourceEventDisplay()
      }

      this.selectedSourceOrg.set(org)
      this.sourceOrgControl.setValue(org.id.toString())

      // Enable/disable source event control based on selection
      if (this.canSelectSourceEvent()) {
        this.sourceEventControl.enable()
        // Load events for the new organization when switching
        if (isOrgChanging && !this.isLoadingSourceEvents()) {
          this.isLoadingSourceEvents.set(true)
          this._loadSourceEvents()
        }
      } else {
        this.sourceEventControl.disable()
      }
      // Reset target if it matches the new source
      if (this.selectedTargetOrg()?.id === org.id) {
        this.clearTargetOrg()
      } else {
        this._resetTargetOrgs()
        // Reload target orgs if source event is already selected (canSelectTarget is true)
        if (this.canSelectTarget() && !this.isLoadingTargetOrgs()) {
          this.isLoadingTargetOrgs.set(true)
          this._loadTargetOrgs()
        }
      }
      // Trigger change detection to update select-input disabled state
      this._cdr.detectChanges()
    }
  }

  /**
   * Handles search event from select-input for source organization.
   * @param {string} searchTerm - The search term.
   */
  public onSourceOrgSearch(searchTerm: string): void {
    // Set loading state IMMEDIATELY when user starts typing to show "Loading..." right away
    this.hasAttemptedLoadSourceOrgs = false
    this.isLoadingSourceOrgs.set(true)
    this._cdr.detectChanges()
    this.sourceOrgSearch.set(searchTerm)
    this._sourceOrgSearch$.next(searchTerm)
    // Set up scroll listener when dropdown opens (search triggers dropdown)
    setTimeout(() => this._setupScrollListener('sourceOrg'), 200)
  }

  /**
   * Handles value changed event from select-input for source event.
   * @param {number | string | null} value - The selected event ID.
   */
  public onSourceEventValueChanged(value: number | string | null): void {
    if (!value) {
      this.selectedSourceEvent.set(null)
      this.sourceEventControl.setValue('')
      return
    }
    const event = this.sourceEventValues().find((e) => e.id === value)
    if (event) {
      const wasEventSelected = this.selectedSourceEvent() !== null
      this.selectedSourceEvent.set(event)
      this.sourceEventControl.setValue(event.id.toString())
      // Load target orgs when source event is first selected (if not already loading)
      if (!wasEventSelected && this.canSelectTarget() && this.targetOrgValues().length === 0 && !this.isLoadingTargetOrgs()) {
        this.isLoadingTargetOrgs.set(true)
        this._loadTargetOrgs()
      }
    }
  }

  /**
   * Handles search event from select-input for source event.
   * @param {string} searchTerm - The search term.
   */
  public onSourceEventSearch(searchTerm: string): void {
    // Set loading state IMMEDIATELY when user starts typing to show "Loading..." right away
    if (this.canSelectSourceEvent()) {
      this.hasAttemptedLoadSourceEvents = false
      this.isLoadingSourceEvents.set(true)
      this._cdr.detectChanges()
    }
    // Load events on first focus if source org is selected
    if (!searchTerm && this.sourceEventValues().length === 0 && !this.isLoadingSourceEvents() && this.canSelectSourceEvent()) {
      this._loadSourceEvents()
    }
    this.sourceEventSearch.set(searchTerm)
    this._sourceEventSearch$.next(searchTerm)
    // Set up scroll listener when dropdown opens (search triggers dropdown)
    setTimeout(() => this._setupScrollListener('sourceEvent'), 200)
  }

  /**
   * Handles value changed event from select-input for target organization.
   * @param {number | string | null} value - The selected organization ID.
   */
  public onTargetOrgValueChanged(value: number | string | null): void {
    if (!value) {
      this.selectedTargetOrg.set(null)
      this.targetOrgControl.setValue('')
      return
    }
    const org = this.targetOrgValues().find((o) => o.id === value)
    const sourceOrg = this.selectedSourceOrg()

    // Prevent selecting same as source
    if (sourceOrg && sourceOrg.id === org?.id) {
      this.targetOrgControl.setValue('')
      return
    }

    if (org) {
      this.selectedTargetOrg.set(org)
      this.targetOrgControl.setValue(org.id.toString())
    }
  }

  /**
   * Handles search event from select-input for target organization.
   * @param {string} searchTerm - The search term.
   */
  public onTargetOrgSearch(searchTerm: string): void {
    // Set loading state IMMEDIATELY when user starts typing to show "Loading..." right away
    if (this.canSelectTarget()) {
      this.isLoadingTargetOrgs.set(true)
      this._cdr.detectChanges()
    }
    // Load organizations on first focus (empty search and empty values)
    if (!searchTerm && this.targetOrgValues().length === 0 && !this.isLoadingTargetOrgs()) {
      this._loadTargetOrgs()
    }
    this.targetOrgSearch.set(searchTerm)
    this._targetOrgSearch$.next(searchTerm)
    // Set up scroll listener when dropdown opens (search triggers dropdown)
    setTimeout(() => this._setupScrollListener('targetOrg'), 300)
  }

  /**
   * Sets up a search subscription.
   * @param {Subject<string>} subject - The subject to subscribe to.
   * @param {Function} callback - The callback to execute when the subject emits a value.
   */
  private _setupSearchSubscription(subject: Subject<string>, callback: (term: string) => void): void {
    subject.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._destroy$)).subscribe(callback)
  }

  /**
   * Loads the source organizations.
   */
  private _loadSourceOrgs(): void {
    this._loadOrganizations({
      isLoading: this.isLoadingSourceOrgs,
      hasMore: () => this._hasMoreSourceOrgs,
      page: () => this._sourceOrgPage,
      setPage: (p) => {
        this._sourceOrgPage = p
      },
      setHasMore: (v) => {
        this._hasMoreSourceOrgs = v
      },
      searchTerm: () => this.sourceOrgSearch(),
      orgs: () => this._sourceOrgs,
      setOrgs: (orgs) => {
        this._sourceOrgs = orgs
      },
      options: this.sourceOrgOptions,
      filterSourceOrg: false
    })
  }

  /**
   * Generic method to load organizations (source or target).
   */
  private _loadOrganizations(config: {
    isLoading: ReturnType<typeof signal<boolean>>
    hasMore: () => boolean
    page: () => number
    setPage: (page: number) => void
    setHasMore: (value: boolean) => void
    searchTerm: () => string
    orgs: () => Workspace[]
    setOrgs: (orgs: Workspace[]) => void
    options: ReturnType<typeof signal<DropdownOption[]>>
    filterSourceOrg: boolean
  }): void {
    // Allow loading if already loading but we have no data yet (initial load)
    if (config.isLoading() && config.orgs().length > 0) return

    // Reset hasMore if we have no orgs (allows reloading after clearing search)
    if (config.orgs().length === 0) {
      // Reset pagination to allow reloading
      config.setPage(0)
      config.setHasMore(true)
    }

    if (!config.hasMore()) return

    config.isLoading.set(true)
    config.setPage(config.page() + 1)
    const page = config.page()

    this._workspaceService
      .getWorkspaces(null, page, config.searchTerm() || null)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          const sourceOrg = this.selectedSourceOrg()
          let newOrgs = response.data || []

          // Filter out source organization if needed
          if (config.filterSourceOrg && sourceOrg) {
            newOrgs = newOrgs.filter((org) => org.id !== sourceOrg.id)
            const filteredExisting = config.orgs().filter((org) => org.id !== sourceOrg.id)
            config.setOrgs([...filteredExisting, ...newOrgs])
          } else {
            config.setOrgs([...config.orgs(), ...newOrgs])
          }

          // Update values arrays for select-input with icons
          const orgsWithIcons = newOrgs.map((org) => ({
            ...org,
            icon: this._getOrgIcon(org)
          }))

          if (config.filterSourceOrg) {
            // Target orgs
            this.targetOrgValues.update((values) => {
              const filtered = values.filter((org) => !sourceOrg || org.id !== sourceOrg.id)
              return [...filtered, ...orgsWithIcons]
            })
          } else {
            // Source orgs
            this.sourceOrgValues.update((values) => [...values, ...orgsWithIcons])
          }

          const options = newOrgs.map((org) => this._orgToOption(org))
          config.options.update((opts) => {
            // Filter existing options if filtering source org
            if (config.filterSourceOrg && sourceOrg) {
              const filtered = opts.filter((opt) => (opt.data as Workspace).id !== sourceOrg.id)
              return [...filtered, ...options]
            }
            return [...opts, ...options]
          })

          config.setHasMore(response.current_page < response.last_page)
          config.isLoading.set(false)
          // Mark that we've attempted to load
          if (!config.filterSourceOrg) {
            this.hasAttemptedLoadSourceOrgs = true
          }
        },
        error: () => {
          config.isLoading.set(false)
          // Mark that we've attempted to load even on error
          if (!config.filterSourceOrg) {
            this.hasAttemptedLoadSourceOrgs = true
          }
        }
      })
  }

  /**
   * Generic method to load events (source or target).
   */
  private _loadEvents(config: {
    org: Workspace | null
    isLoading: ReturnType<typeof signal<boolean>>
    hasMore: () => boolean
    page: () => number
    setPage: (page: number) => void
    setHasMore: (value: boolean) => void
    searchTerm: () => string
    events: () => ServerRepresentedEvent[]
    setEvents: (events: ServerRepresentedEvent[]) => void
    options: ReturnType<typeof signal<DropdownOption[]>>
  }): void {
    if (!config.org) return

    // Allow loading if already loading but we have no data yet (initial load)
    if (config.isLoading() && config.events().length > 0) return

    // Reset hasMore if we have no events (allows reloading after clearing search or switching orgs)
    if (config.events().length === 0) {
      // Reset pagination to allow reloading
      config.setPage(0)
      config.setHasMore(true)
    }

    if (!config.hasMore()) return

    config.isLoading.set(true)
    config.setPage(config.page() + 1)
    const page = config.page()

    this._eventService
      .getEvents(page, config.searchTerm() || null, 0, false, 20, null, config.org.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          const newEvents = response.data || []
          config.setEvents([...config.events(), ...newEvents])

          // Update values array for select-input with banners and status dots
          const eventsWithIcons = newEvents.map((event) => ({
            ...event,
            icon: this._getEventIcon(event)
          }))
          this.sourceEventValues.update((values) => [...values, ...eventsWithIcons])

          const options = newEvents.map((event) => this._eventToOption(event))
          config.options.update((opts) => [...opts, ...options])
          config.setHasMore(response.current_page < response.last_page)
          config.isLoading.set(false)
          // Mark that we've attempted to load source events
          this.hasAttemptedLoadSourceEvents = true
        },
        error: () => {
          config.isLoading.set(false)
          // Mark that we've attempted to load even on error
          this.hasAttemptedLoadSourceEvents = true
        }
      })
  }

  /**
   * Loads the source events.
   */
  private _loadSourceEvents(): void {
    this._loadEvents({
      org: this.selectedSourceOrg(),
      isLoading: this.isLoadingSourceEvents,
      hasMore: () => this._hasMoreSourceEvents,
      page: () => this._sourceEventPage,
      setPage: (p) => {
        this._sourceEventPage = p
      },
      setHasMore: (v) => {
        this._hasMoreSourceEvents = v
      },
      searchTerm: () => this.sourceEventSearch(),
      events: () => this._sourceEvents,
      setEvents: (events) => {
        this._sourceEvents = events
      },
      options: this.sourceEventOptions
    })
  }

  /**
   * Loads the target organizations.
   */
  private _loadTargetOrgs(): void {
    this._loadOrganizations({
      isLoading: this.isLoadingTargetOrgs,
      hasMore: () => this._hasMoreTargetOrgs,
      page: () => this._targetOrgPage,
      setPage: (p) => {
        this._targetOrgPage = p
      },
      setHasMore: (v) => {
        this._hasMoreTargetOrgs = v
      },
      searchTerm: () => this.targetOrgSearch(),
      orgs: () => this._targetOrgs,
      setOrgs: (orgs) => {
        this._targetOrgs = orgs
      },
      options: this.targetOrgOptions,
      filterSourceOrg: true
    })
  }

  /**
   * Resets the source organizations.
   */
  private _resetSourceOrgs(): void {
    this._sourceOrgs = []
    // Only clear options if not loading (to prevent showing "No organizations found" during loading)
    if (!this.isLoadingSourceOrgs()) {
      this.sourceOrgOptions.set([])
    }
    this.sourceOrgValues.set([])
    this._sourceOrgPage = 0
    this._hasMoreSourceOrgs = true
    this.hasAttemptedLoadSourceOrgs = false
    // Don't reset loading state here - it should be set before calling reset when loading
    // Only reset if we're not currently loading (e.g., when clearing without loading)
    if (!this.isLoadingSourceOrgs()) {
      this.isLoadingSourceOrgs.set(false)
    }
  }

  /**
   * Resets the source events.
   */
  private _resetSourceEvents(): void {
    this._sourceEvents = []
    // Only clear options if not loading (to prevent showing "No events found" during loading)
    if (!this.isLoadingSourceEvents()) {
      this.sourceEventOptions.set([])
    }
    this.sourceEventValues.set([])
    this._sourceEventPage = 0
    this._hasMoreSourceEvents = true
    this.hasAttemptedLoadSourceEvents = false
    // Don't reset loading state here - it should be set before calling reset when loading
    // Only reset if we're not currently loading (e.g., when clearing without loading)
    if (!this.isLoadingSourceEvents()) {
      this.isLoadingSourceEvents.set(false)
    }
  }

  /**
   * Resets the target organizations.
   */
  private _resetTargetOrgs(): void {
    this._targetOrgs = []
    // Only clear options if not loading (to prevent showing "No organizations found" during loading)
    if (!this.isLoadingTargetOrgs()) {
      this.targetOrgOptions.set([])
    }
    this.targetOrgValues.set([])
    this._targetOrgPage = 0
    this._hasMoreTargetOrgs = true
    // Don't reset loading state here - it should be set before calling reset when loading
    // Only reset if we're not currently loading (e.g., when clearing without loading)
    if (!this.isLoadingTargetOrgs()) {
      this.isLoadingTargetOrgs.set(false)
    }
    // Clear the FormControl value to prevent showing ID when values array is empty
    if (this.targetOrgControl.value) {
      this.targetOrgControl.setValue('', { emitEvent: true })
      this.selectedTargetOrg.set(null)
    }
  }

  /**
   * Clears the source event display value in the UI.
   * This is needed because the select-input component's resetSelection() doesn't clear the selectedValue property.
   */
  private _clearSourceEventDisplay(): void {
    setTimeout(() => {
      if (this.sourceEventSelectInput) {
        // Clear the component's internal selectedValue property (used for display)
        ;(this.sourceEventSelectInput as any).selectedValue = ''
      }
      this._cdr.detectChanges()
    }, 0)
  }

  /**
   * Converts an organization to a dropdown option.
   * @param {Workspace} org - The organization to convert.
   * @returns {DropdownOption} The dropdown option.
   */
  private _orgToOption(org: Workspace): DropdownOption {
    const name = this._getOrgName(org)
    const initials = name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)

    const icon = org.logo
      ? `<img src="${org.logo}" alt="${name}" style="width: 28px; height: 28px; border-radius: 6px; object-fit: cover; flex-shrink: 0; display: block;"/>`
      : `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; display: block;"><rect width="28" height="28" fill="#e5e7eb" rx="6"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="10" font-weight="600" fill="#374151">${initials}</text></svg>`

    return {
      key: org.id,
      value: name,
      prefixIcon: icon as any,
      data: org
    }
  }

  /**
   * Converts an event to a dropdown option.
   * @param {ServerRepresentedEvent} event - The event to convert.
   * @returns {DropdownOption} The dropdown option.
   */
  private _eventToOption(event: ServerRepresentedEvent): DropdownOption {
    const status = this._getEventStatus(event)
    const statusColor = this._getStatusColor(status)
    const dot = `<span style="display: inline-flex; align-items: center; justify-content: center; width: 10px; height: 10px; border-radius: 50%; background-color: ${statusColor}; flex-shrink: 0; margin-left: 8px;"></span>`

    const icon =
      event.cover_page || event.logo
        ? `<img src="${event.cover_page || event.logo}" alt="${
            event.name
          }" style="width: 28px; height: 28px; border-radius: 6px; object-fit: cover; flex-shrink: 0; display: block;"/>`
        : `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; display: block;"><rect width="28" height="28" fill="#f3f4f6" rx="6"/><path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V8H19V19Z" fill="#9ca3af" transform="translate(5.5, 5.5) scale(0.7)"/></svg>`

    return {
      key: event.id,
      value: `${event.name || ''}${dot}`,
      prefixIcon: icon as any,
      data: event
    }
  }

  /**
   * Gets the name of an organization.
   * @param {Workspace} org - The organization to get the name of.
   * @returns {string} The name of the organization.
   */
  private _getOrgName(org: Workspace): string {
    return org.name || org.slug || `Organization ${org.id}`
  }

  /**
   * Gets the icon for an organization (logo or initials).
   * @param {Workspace} org - The organization to get the icon for.
   * @returns {string} The icon HTML string.
   */
  private _getOrgIcon(org: Workspace): string {
    const name = this._getOrgName(org)
    const initials = name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .substring(0, 2)

    if (org.logo) {
      return `<img src="${org.logo}" alt="${name}" style="width: 28px; height: 28px; border-radius: 6px; object-fit: cover; flex-shrink: 0; display: block;"/>`
    }

    return `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; display: block;"><rect width="28" height="28" fill="#e5e7eb" rx="6"/><text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="10" font-weight="600" fill="#374151">${initials}</text></svg>`
  }

  /**
   * Gets the icon for an event (banner/logo with status dot overlay).
   * @param {ServerRepresentedEvent} event - The event to get the icon for.
   * @returns {string} The icon HTML string.
   */
  private _getEventIcon(event: ServerRepresentedEvent): string {
    const status = this._getEventStatus(event)
    const statusColor = this._getStatusColor(status)
    const statusDot = `<svg width="10" height="10" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" style="position: absolute; bottom: -2px; right: -2px; flex-shrink: 0;"><circle cx="5" cy="5" r="5" fill="${statusColor}"/></svg>`

    if (event.cover_page || event.logo) {
      return `<div style="position: relative; width: 28px; height: 28px; flex-shrink: 0; display: block;"><img src="${event.cover_page || event.logo}" alt="${
        event.name
      }" style="width: 28px; height: 28px; border-radius: 6px; object-fit: cover; display: block;"/>${statusDot}</div>`
    }

    // Default event icon with status dot
    const defaultIcon = `<svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0; display: block;"><rect width="28" height="28" fill="#f3f4f6" rx="6"/><path d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,19H5V8H19V19Z" fill="#9ca3af" transform="translate(5.5, 5.5) scale(0.7)"/></svg>`
    return `<div style="position: relative; width: 28px; height: 28px; flex-shrink: 0; display: block;">${defaultIcon}${statusDot}</div>`
  }

  /**
   * Gets the status of an event.
   * @param {ServerRepresentedEvent} event - The event to get the status of.
   * @returns {string} The status of the event.
   */
  private _getEventStatus(event: ServerRepresentedEvent): string {
    if (!event.public_start_date || !event.public_end_date) return 'unknown'
    const now = new Date()
    const start = new Date(event.public_start_date)
    const end = new Date(event.public_end_date)
    if (now > end) return 'finished'
    if (now >= start && now <= end) return 'live'
    return 'upcoming'
  }

  /**
   * Gets the color of a status.
   * @param {string} status - The status to get the color of.
   * @returns {string} The color of the status.
   */
  private _getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      live: '#10b981',
      upcoming: '#fbbf24',
      finished: '#6b7280'
    }
    return colors[status.toLowerCase()] || '#9ca3af'
  }

  /**
   * Sets up a scroll listener.
   * @param {string} type - The type of scroll listener to set up.
   */
  private _setupScrollListener(type: 'sourceOrg' | 'sourceEvent' | 'targetOrg'): void {
    const handlers = {
      sourceOrg: () => this._sourceOrgScrollHandler,
      sourceEvent: () => this._sourceEventScrollHandler,
      targetOrg: () => this._targetOrgScrollHandler
    }

    const loaders = {
      sourceOrg: () => this._loadSourceOrgs(),
      sourceEvent: () => this._loadSourceEvents(),
      targetOrg: () => this._loadTargetOrgs()
    }

    const checks = {
      sourceOrg: () => this._hasMoreSourceOrgs && !this.isLoadingSourceOrgs(),
      sourceEvent: () => this._hasMoreSourceEvents && !this.isLoadingSourceEvents(),
      targetOrg: () => this._hasMoreTargetOrgs && !this.isLoadingTargetOrgs()
    }

    const setters = {
      sourceOrg: (h: (e: Event) => void) => {
        this._sourceOrgScrollHandler = h
      },
      sourceEvent: (h: (e: Event) => void) => {
        this._sourceEventScrollHandler = h
      },
      targetOrg: (h: (e: Event) => void) => {
        this._targetOrgScrollHandler = h
      }
    }

    // Remove existing handler from all containers
    const existing = handlers[type]()
    if (existing) {
      document.querySelectorAll('.dropdown-list-container').forEach((container) => {
        container.removeEventListener('scroll', existing)
      })
    }

    // Use longer delay for targetOrg since it's conditionally rendered
    const delay = type === 'targetOrg' ? 300 : 200

    setTimeout(() => {
      // Find all dropdown containers and get the one that's currently visible/open
      const allContainers = Array.from(document.querySelectorAll('.dropdown-list-container')) as HTMLElement[]

      // Find all visible containers (not hidden, has display, and is in viewport)
      const visibleContainers = allContainers.filter((container) => {
        const styles = window.getComputedStyle(container)
        return styles.display !== 'none' && styles.visibility !== 'hidden' && container.offsetHeight > 0
      })

      // Get the last visible container (most recently opened)
      const container = visibleContainers[visibleContainers.length - 1]

      if (!container) {
        // Retry for conditionally rendered dropdowns
        if (type === 'targetOrg') {
          setTimeout(() => this._setupScrollListener(type), 200)
        }
        return
      }

      // For targetOrg, enforce height constraint if not scrollable
      if (type === 'targetOrg') {
        const scrollHeight = container.scrollHeight
        const clientHeight = container.clientHeight

        // If content exceeds 320px but container isn't scrollable, enforce height constraint
        if (scrollHeight > 320 && scrollHeight <= clientHeight) {
          container.style.setProperty('height', '320px', 'important')
          container.style.setProperty('overflow-y', 'auto', 'important')
          setTimeout(() => this._setupScrollListener(type), 100)
          return
        }
      }

      // Create scroll handler
      const handler = (e: Event) => {
        const el = e.target as HTMLElement
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
          if (checks[type]()) loaders[type]()
        }
      }

      // Store handler and attach listener
      setters[type](handler)
      container.addEventListener('scroll', handler, { passive: true })
    }, delay)
  }

  /**
   * Cleans up a scroll listener.
   * @param {string} type - The type of scroll listener to clean up.
   */
  private _cleanupScrollListener(type: 'sourceOrg' | 'sourceEvent' | 'targetOrg'): void {
    const handlers = {
      sourceOrg: () => this._sourceOrgScrollHandler,
      sourceEvent: () => this._sourceEventScrollHandler,
      targetOrg: () => this._targetOrgScrollHandler
    }

    const setters = {
      sourceOrg: () => {
        this._sourceOrgScrollHandler = undefined
      },
      sourceEvent: () => {
        this._sourceEventScrollHandler = undefined
      },
      targetOrg: () => {
        this._targetOrgScrollHandler = undefined
      }
    }

    const handler = handlers[type]()
    if (handler) {
      const container = document.querySelector('.dropdown-list-container') as HTMLElement
      if (container) container.removeEventListener('scroll', handler)
      setters[type]()
    }
  }

  /**
   * Cleans up all scroll listeners.
   */
  private _cleanupScrollListeners(): void {
    this._cleanupScrollListener('sourceOrg')
    this._cleanupScrollListener('sourceEvent')
    this._cleanupScrollListener('targetOrg')
  }

  /**
   * Handles the migrate event button click.
   */
  public onMigrateEvent(): void {
    if (!this.canMigrate() || this._isMigrating) return

    this._isMigrating = true
    this._eventMigrationService
      .migrateEvent(this.selectedSourceOrg()!.slug, this.selectedTargetOrg()!.slug, this.selectedSourceEvent()!.slug)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (response) => {
          this._alertService.alert(Alert.SUCCESS, response.message)
          this._authService.currentWorkspace = this.selectedTargetOrg()!
          this._isMigrating = false
          const eventId = response.event_id
          const organizationId = this.selectedTargetOrg()!.id
          const eventName = this.selectedSourceEvent()?.name ?? 'Event'
          this._router.navigate(['/events/list']).finally(() => {
            if (!eventId) return
            setTimeout(() => {
              this._wizardService.open({
                component: EventWizardComponent,
                componentInputs: {
                  eventId,
                  organizationId,
                  industrySectors: this._appService.industrySectors
                },
                headerConfig: {
                  breadcrumbs: [eventName]
                }
              })
            })
          })
        },
        error: (error) => {
          this._isMigrating = false
          this._alertService.alert(Alert.ERROR, error.message)
        }
      })
  }
}
